import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateCustomerRequest,
  CreateCustomerRequestSchema,
  type CustomerDetail,
  type UpdateCustomerRequest,
  UpdateCustomerRequestSchema,
} from '@utazzo/shared';
import {
  UzCollapsibleSection,
  UzCombobox,
  UzForm,
  UzFormField,
  UzInput,
  UzSelect,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect, useMemo } from 'react';
import { type Resolver, useForm, useFormContext, useWatch } from 'react-hook-form';

import { useCountries, useCreateCustomer, useUpdateCustomer } from '../use-customers';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SALUTATION_OPTIONS = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
];

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

const MARITAL_OPTIONS = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'MARRIED', label: 'Married' },
  { value: 'DIVORCED', label: 'Divorced' },
  { value: 'WIDOWED', label: 'Widowed' },
  { value: 'OTHER', label: 'Other' },
];

const KIND_OPTIONS = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'CORPORATE', label: 'Corporate' },
];

const CREATE_DEFAULTS: CreateCustomerRequest = { kind: 'INDIVIDUAL' };

// ---------------------------------------------------------------------------
// Shared inner field set (reads form via context)
// ---------------------------------------------------------------------------

interface ProfileFieldsProps {
  pending: boolean;
}

/**
 * Renders the full profile field set inside whichever UzForm wraps it.
 * The kind toggle (INDIVIDUAL/CORPORATE) is watched; sections that only apply
 * to one kind are conditionally rendered.
 */
const ProfileFields = ({ pending }: ProfileFieldsProps): React.JSX.Element => {
  const { setValue } = useFormContext();
  const kind = useWatch({ name: 'kind' }) as string | undefined;
  const isIndividual = kind !== 'CORPORATE';

  const countriesQuery = useCountries();

  const countryOptions = useMemo(
    () =>
      (countriesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
      })),
    [countriesQuery.data],
  );

  return (
    <div className="space-y-4">
      {/* Kind toggle */}
      <UzFormField name="kind">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Customer type"
            required
            options={KIND_OPTIONS}
            value={(field.value as string) || 'INDIVIDUAL'}
            onValueChange={(v) => {
              field.onChange(v);
              // Clear kind-specific fields when toggling.
              if (v === 'CORPORATE') {
                setValue('firstName', null, { shouldValidate: false });
                setValue('lastName', null, { shouldValidate: false });
                setValue('middleName', null, { shouldValidate: false });
              } else {
                setValue('companyName', null, { shouldValidate: false });
                setValue('contactPerson', null, { shouldValidate: false });
              }
            }}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>

      {/* INDIVIDUAL fields */}
      {isIndividual && (
        <UzCollapsibleSection title="Personal details" description="Name, date of birth, gender">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UzFormField name="salutation">
              {({ field, error }) => (
                <UzSelect
                  name={field.name}
                  label="Salutation"
                  options={SALUTATION_OPTIONS}
                  value={(field.value as string) || undefined}
                  onValueChange={field.onChange}
                  placeholder="Select"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="firstName">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null | undefined) ?? ''}
                  label="First name"
                  placeholder="e.g. Priya"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="middleName">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null | undefined) ?? ''}
                  label="Middle name"
                  placeholder="Optional"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="lastName">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null | undefined) ?? ''}
                  label="Last name"
                  placeholder="e.g. Sharma"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="dateOfBirth">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  type="date"
                  value={(field.value as string | null | undefined) ?? ''}
                  label="Date of birth"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="gender">
              {({ field, error }) => (
                <UzSelect
                  name={field.name}
                  label="Gender"
                  options={GENDER_OPTIONS}
                  value={(field.value as string) || undefined}
                  onValueChange={field.onChange}
                  placeholder="Select"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="maritalStatus">
              {({ field, error }) => (
                <UzSelect
                  name={field.name}
                  label="Marital status"
                  options={MARITAL_OPTIONS}
                  value={(field.value as string) || undefined}
                  onValueChange={field.onChange}
                  placeholder="Select"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
        </UzCollapsibleSection>
      )}

      {/* CORPORATE fields */}
      {!isIndividual && (
        <UzCollapsibleSection
          title="Company details"
          description="Company name and primary contact"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UzFormField name="companyName">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null | undefined) ?? ''}
                  label="Company name"
                  placeholder="e.g. Acme Travel Pvt Ltd"
                  required
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="contactPerson">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null | undefined) ?? ''}
                  label="Contact person"
                  placeholder="e.g. Rahul Mehta"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
        </UzCollapsibleSection>
      )}

      {/* Contact info - always visible */}
      <UzCollapsibleSection title="Contact information" description="Email and phone numbers">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <UzFormField name="email">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  type="email"
                  value={(field.value as string | null | undefined) ?? ''}
                  label="Email"
                  placeholder="customer@example.com"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <UzFormField name="mobileCountry">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null | undefined) ?? ''}
                  label="Country code"
                  placeholder="91"
                  maxLength={4}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <div className="col-span-2">
              <UzFormField name="mobileNumber">
                {({ field, error }) => (
                  <UzInput
                    {...field}
                    value={(field.value as string | null | undefined) ?? ''}
                    label="Mobile number"
                    placeholder="9876543210"
                    error={error}
                    disabled={pending}
                  />
                )}
              </UzFormField>
            </div>
          </div>
          <UzFormField name="altPhone">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                label="Alternate phone"
                placeholder="Optional"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      </UzCollapsibleSection>

      {/* Identity numbers */}
      <UzCollapsibleSection title="Identity" description="Passport, PAN, GST, and nationality">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <UzFormField name="nationalityCountryId">
            {({ field, error }) => (
              <UzCombobox
                name={field.name}
                label="Nationality"
                placeholder={countriesQuery.isLoading ? 'Loading countries…' : 'Select a country'}
                searchPlaceholder="Search countries…"
                options={countryOptions}
                value={(field.value as string | null | undefined) || undefined}
                onValueChange={field.onChange}
                loading={countriesQuery.isLoading}
                emptyText="No country found"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="passportNumber">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                label="Passport number"
                placeholder="e.g. A1234567"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="panNumber">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                label="PAN number"
                placeholder="e.g. AABCT1234D"
                maxLength={10}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="gstNumber">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                label="GST number"
                placeholder="e.g. 27AAAAA0000A1Z5"
                maxLength={15}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      </UzCollapsibleSection>

      {/* Notes */}
      <UzCollapsibleSection title="Notes" description="Internal notes">
        <UzFormField name="notes">
          {({ field, error }) => (
            <UzTextarea
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              label="Notes"
              placeholder="Internal notes visible to agents only"
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </UzCollapsibleSection>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface ProfileStepProps {
  /** Existing customer (edit mode) or null (create mode). */
  customer: CustomerDetail | null;
  /** Called with the customer id after a successful create or update. */
  onSaved: (customerId: string) => void;
}

/**
 * Step 0 of the customer stepper.
 *
 * - Create mode: POSTs a new customer and calls onSaved with the returned id
 *   so subsequent steps can attach sub-resources.
 * - Edit mode: PATCHes the existing customer; onSaved is called with the same
 *   id to advance the stepper.
 *
 * The form uses the Create schema for new records and the Update schema for
 * edits, matching the backend's separate validation paths.
 */
export const ProfileStep = ({ customer, onSaved }: ProfileStepProps): React.JSX.Element => {
  const isEdit = Boolean(customer);
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const pending = createCustomer.isPending || updateCustomer.isPending;

  const createForm = useForm<CreateCustomerRequest>({
    // `kind` has a Zod default, so the resolver's input type diverges from the
    // inferred output type; cast to the output-typed resolver.
    resolver: zodResolver(CreateCustomerRequestSchema) as Resolver<CreateCustomerRequest>,
    defaultValues: CREATE_DEFAULTS,
  });

  const updateForm = useForm<UpdateCustomerRequest>({
    resolver: zodResolver(UpdateCustomerRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!customer) {
      createForm.reset(CREATE_DEFAULTS);
      return;
    }
    updateForm.reset({
      kind: customer.kind,
      salutation: customer.salutation ?? undefined,
      firstName: customer.firstName ?? undefined,
      middleName: customer.middleName ?? undefined,
      lastName: customer.lastName ?? undefined,
      email: customer.email ?? undefined,
      mobileCountry: customer.mobileCountry ?? undefined,
      mobileNumber: customer.mobileNumber ?? undefined,
      altPhone: customer.altPhone ?? undefined,
      dateOfBirth: customer.dateOfBirth ?? undefined,
      gender: (customer.gender ?? undefined) as UpdateCustomerRequest['gender'],
      maritalStatus: (customer.maritalStatus ??
        undefined) as UpdateCustomerRequest['maritalStatus'],
      nationalityCountryId: customer.nationality?.id ?? undefined,
      passportNumber: customer.passportNumber ?? undefined,
      panNumber: customer.panNumber ?? undefined,
      gstNumber: customer.gstNumber ?? undefined,
      companyName: customer.companyName ?? undefined,
      contactPerson: customer.contactPerson ?? undefined,
      notes: customer.notes ?? undefined,
    });
  }, [customer, createForm, updateForm]);

  const handleCreate = createForm.handleSubmit(async (values) => {
    const result = await createCustomer.mutateAsync(values);
    onSaved(result.id);
  });

  const handleUpdate = updateForm.handleSubmit(async (values) => {
    if (!customer) return;
    const result = await updateCustomer.mutateAsync({ id: customer.id, payload: values });
    onSaved(result.id);
  });

  if (isEdit) {
    return (
      <UzForm form={updateForm} onSubmit={handleUpdate}>
        <ProfileFields pending={pending} />
        {/* The submit button is in CustomerStepperNav; expose the handler via a hidden button. */}
        <button type="submit" id="profile-submit" className="sr-only" aria-hidden tabIndex={-1} />
      </UzForm>
    );
  }

  return (
    <UzForm form={createForm} onSubmit={handleCreate}>
      <ProfileFields pending={pending} />
      <button type="submit" id="profile-submit" className="sr-only" aria-hidden tabIndex={-1} />
    </UzForm>
  );
};

// Expose the submit trigger id so the stepper nav can fire it.
export const PROFILE_SUBMIT_ID = 'profile-submit';

// Expose pending accessor type for the stepper page.
export interface ProfileStepHandle {
  isPending: boolean;
}

// Export the hook so the stepper page can check pending state separately.
export const useProfileStepPending = (): boolean => {
  const c = useCreateCustomer();
  const u = useUpdateCustomer();
  return c.isPending || u.isPending;
};

// Export defaults for tests.
export { CREATE_DEFAULTS as PROFILE_CREATE_DEFAULTS };
